import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { contractsApi, type PaginatedContractsResponse } from "@/lib/api";
import type { Contract } from "@/types";
import { useOrganizationStore } from "@/stores/organizationStore";
import type { ContractStatus, ContractSummaryView, ContractType } from "@/types";

import { DEFAULT_PAGE_SIZE, PREFETCH_STALE_TIME } from "@/lib/constants";

interface ContractsFilters {
  search?: string;
  pageSize?: number;
  page?: number;
  status?: ContractStatus[];
  contractType?: ContractType[];
  ordering?: string;
  withoutTracks?: boolean;
  startDateAfter?: string;
  startDateBefore?: string;
  endDateAfter?: string;
  endDateBefore?: string;
  endDateActive?: boolean;
  summary?: ContractSummaryView;
}

interface UseContractsOptions extends ContractsFilters {
  enabled?: boolean;
  staleTime?: number;
}

interface ContractsQueryOptionsInput extends ContractsFilters {
  labelId: string | undefined;
  staleTime?: number;
}

interface UseContractsReturn {
  contracts: Contract[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

// Helper function to create normalized query keys
const createContractsQueryKey = (
  labelId: string | undefined,
  search: string | undefined,
  pageSize: number | undefined,
  page: number | undefined,
  status: ContractStatus[] | undefined,
  contractType: ContractType[] | undefined,
  ordering: string | undefined,
  withoutTracks: boolean | undefined,
  startDateAfter: string | undefined,
  startDateBefore: string | undefined,
  endDateAfter: string | undefined,
  endDateBefore: string | undefined,
  endDateActive: boolean | undefined,
  summary: ContractSummaryView | undefined,
) => {
  const key: (string | number)[] = ["contracts"];

  // Always include labelId (required for most queries)
  if (labelId) key.push("label", labelId);

  // Normalize search - convert undefined/empty to consistent value
  const normalizedSearch = search?.trim() || null;
  if (normalizedSearch) key.push("search", normalizedSearch);

  // Add pageSize if specified and different from default
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) key.push("pageSize", pageSize);

  if (page && page > 1) key.push("page", page);

  // Add filter parameters
  if (status && status.length > 0) key.push("status", status.sort().join(","));
  if (contractType && contractType.length > 0)
    key.push("contractType", contractType.sort().join(","));
  if (ordering) key.push("ordering", ordering);
  if (withoutTracks) key.push("withoutTracks", "true");
  if (startDateAfter) key.push("startDateAfter", startDateAfter);
  if (startDateBefore) key.push("startDateBefore", startDateBefore);
  if (endDateAfter) key.push("endDateAfter", endDateAfter);
  if (endDateBefore) key.push("endDateBefore", endDateBefore);
  if (endDateActive) key.push("endDateActive", "true");
  if (summary) key.push("summary", summary);

  return key;
};

export const contractsQueryOptions = ({
  labelId,
  staleTime,
  search,
  pageSize,
  page,
  status,
  contractType,
  ordering,
  withoutTracks,
  startDateAfter,
  startDateBefore,
  endDateAfter,
  endDateBefore,
  endDateActive,
  summary,
}: ContractsQueryOptionsInput) => {
  const filters = {
    label_id: labelId,
    ...(search && { q: search }),
    ...(pageSize && { page_size: pageSize }),
    ...(page && { page }),
    ...(status && status.length > 0 && { status: status.join(",") }),
    ...(contractType && contractType.length > 0 && { contract_type: contractType.join(",") }),
    ...(ordering && { ordering }),
    ...(withoutTracks && { without_tracks: true }),
    ...(startDateAfter && { start_date_after: startDateAfter }),
    ...(startDateBefore && { start_date_before: startDateBefore }),
    ...(endDateAfter && { end_date_after: endDateAfter }),
    ...(endDateBefore && { end_date_before: endDateBefore }),
    ...(endDateActive && { end_date_active: true }),
    ...(summary && { summary }),
  };

  const queryKey = createContractsQueryKey(
    labelId,
    search,
    pageSize,
    page,
    status,
    contractType,
    ordering,
    withoutTracks,
    startDateAfter,
    startDateBefore,
    endDateAfter,
    endDateBefore,
    endDateActive,
    summary,
  );

  return {
    queryKey,
    queryFn: (): Promise<PaginatedContractsResponse> => contractsApi.getContracts(filters),
    ...(staleTime !== undefined && { staleTime }),
  };
};

export const usePrefetchContracts = () => {
  const queryClient = useQueryClient();
  const { currentLabel } = useOrganizationStore();

  return (filters: ContractsFilters) => {
    if (!currentLabel) return;

    queryClient.prefetchQuery(
      contractsQueryOptions({
        ...filters,
        labelId: currentLabel.id,
        staleTime: PREFETCH_STALE_TIME,
      }),
    );
  };
};

export const useContracts = ({
  enabled = true,
  ...filters
}: UseContractsOptions = {}): UseContractsReturn => {
  const { currentLabel } = useOrganizationStore();

  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    ...contractsQueryOptions({ ...filters, labelId: currentLabel?.id }),
    enabled: enabled && !!currentLabel,
    placeholderData: keepPreviousData,
  });

  return {
    contracts: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
